import React, { useState, useRef, useEffect } from 'react';
import { formatFileSize } from '../utils/audioUtils';
import '../styles/AudioPicker.css';
import ViewOnceToggle from './ViewOnceToggle';

function AudioPicker({ isVisible, onAudioSelect, onClose, isUploading: externalIsUploading }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [viewOnce, setViewOnce] = useState(false);
  const fileInputRef = useRef(null);

  // Use external uploading state if provided, otherwise use internal state
  const isUploading = externalIsUploading !== undefined ? externalIsUploading : false;

  // Supported audio types
  const supportedTypes = {
    'audio/mp3': 'MP3',
    'audio/mpeg': 'MP3',
    'audio/wav': 'WAV',
    'audio/wave': 'WAV',
    'audio/x-wav': 'WAV',
    'audio/ogg': 'OGG',
    'audio/mp4': 'M4A',
    'audio/m4a': 'M4A',
    'audio/aac': 'AAC',
    'audio/x-aac': 'AAC',
    'audio/flac': 'FLAC',
    'audio/x-flac': 'FLAC',
    'audio/webm': 'WEBM'
  };

  const maxFileSize = 25 * 1024 * 1024; // 25MB limit for audio files
  const maxFiles = 10; // Maximum number of files

  // Reset selected files when component becomes invisible
  useEffect(() => {
    if (!isVisible) {
      setSelectedFiles([]);
    }
  }, [isVisible]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
    // Reset input to allow selecting the same files again
    event.target.value = '';
  };

  const processFiles = (files) => {
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      // Check file type
      if (!supportedTypes[file.type] && !isAudioFile(file)) {
        errors.push(`${file.name}: Unsupported audio type`);
        return;
      }

      // Check file size
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File too large (max 25MB)`);
        return;
      }

      // Check if we're not exceeding the maximum number of files
      if (selectedFiles.length + validFiles.length >= maxFiles) {
        errors.push(`Maximum ${maxFiles} files allowed`);
        return;
      }

      // Get audio duration (will be estimated or set to 0 if unavailable)
      const audioType = supportedTypes[file.type] || getFileExtension(file.name).toUpperCase();
      
      validFiles.push({
        file: file,
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: audioType,
        sizeFormatted: formatFileSize(file.size),
        duration: 0 // Will be updated if we can get actual duration
      });
    });

    if (errors.length > 0) {
      alert('Some files were not added:\n' + errors.join('\n'));
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const isAudioFile = (file) => {
    const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.webm'];
    return audioExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
  };

  const getFileExtension = (filename) => {
    return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2);
  };

  const removeFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const handleSend = () => {
    if (selectedFiles.length > 0 && onAudioSelect && !isUploading) {
      const files = selectedFiles.map(item => item.file);
      onAudioSelect(files, { viewOnce });
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setViewOnce(false);
    onClose();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const files = Array.from(event.dataTransfer.files);
    processFiles(files);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const getFileIcon = () => {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" fill="#e74c3c"/>
        <path d="M19 10v2a7 7 0 01-14 0v-2" stroke="#e74c3c" strokeWidth="2" fill="none"/>
        <line x1="12" y1="19" x2="12" y2="23" stroke="#e74c3c" strokeWidth="2"/>
        <line x1="8" y1="23" x2="16" y2="23" stroke="#e74c3c" strokeWidth="2"/>
      </svg>
    );
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isVisible) return null;

  return (
    <div className="audio-picker-overlay">
      <div className="audio-picker-container">
        {/* Header */}
        <div className="audio-picker-header">
          <h3>Select Audio Files</h3>
          <button className="close-btn" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="audio-picker-content">
          {/* Drop Zone */}
          <div 
            className={`drop-zone ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={openFileDialog}
          >
            <div className="drop-zone-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
                <path d="M19 10v2a7 7 0 01-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
                <line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
              <p>Drop audio files here or click to browse</p>
              <span className="file-types">
                Supported: MP3, WAV, OGG, M4A, AAC, FLAC, WEBM
              </span>
              <span className="file-limit">
                Max {maxFiles} files, 25MB each
              </span>
            </div>
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="selected-files">
              <h4>Selected Files ({selectedFiles.length})</h4>
              <div className="file-list">
                {selectedFiles.map((fileItem) => (
                  <div key={fileItem.id} className="file-item">
                    <div className="file-icon">
                      {getFileIcon()}
                    </div>
                    <div className="file-info">
                      <span className="file-name">{fileItem.name}</span>
                      <span className="file-details">
                        {fileItem.type} • {fileItem.sizeFormatted} • {formatDuration(fileItem.duration)}
                      </span>
                    </div>
                    <button 
                      className="remove-file-btn"
                      onClick={() => removeFile(fileItem.id)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="audio-picker-footer">
          <ViewOnceToggle enabled={viewOnce} onChange={setViewOnce} disabled={isUploading} />
          <button 
            className="cancel-btn" 
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button 
            className="send-btn" 
            onClick={handleSend}
            disabled={selectedFiles.length === 0 || isUploading}
          >
            {isUploading ? (
              <>
                <span className="upload-spinner"></span>
                Sending...
              </>
            ) : (
              `Send ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}`
            )}
          </button>
        </div>

        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac,.webm"
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
}

export default AudioPicker;
